import { useEffect, useRef, useState } from "react";
import { API, errMsg, imgSrc, imageRatioClass } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Eye, EyeOff } from "lucide-react";
import ImageRatioPicker from "@/components/ImageRatioPicker";

const EMPTY = { title: "", excerpt: "", image: "", image_ratio: "16:9", content: "", is_published: true, date_label: "", read_time: "5 menit baca" };

export default function BlogsAdmin() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => API.get("/blog/all").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await API.put(`/blog/${editId}`, form);
      else await API.post("/blog", form);
      toast.success(editId ? "Artikel diperbarui" : "Artikel diterbitkan");
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus artikel ini secara permanen?")) return;
    await API.delete(`/blog/${id}`);
    toast.success("Artikel dihapus");
    load();
  };

  const togglePublish = async (b) => {
    await API.put(`/blog/${b.id}`, { title: b.title, excerpt: b.excerpt || "", image: b.image || "", image_ratio: b.image_ratio || "16:9", content: b.content || "", is_published: !b.is_published, date_label: b.date_label || "", read_time: b.read_time || "" });
    toast.success(b.is_published ? "Artikel disembunyikan (draft)" : "Artikel dipublish");
    load();
  };

  const uploadImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await API.post("/upload", fd);
      setForm({ ...form, image: data.url });
      toast.success("Gambar terupload");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Kelola Blog</h1>
          <p className="text-sm text-slate-500">Tambah, edit, hapus, dan publish artikel — langsung tampil di website</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} data-testid="add-blog" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Tulis Artikel
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="blog-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Artikel</th>
              <th className="px-5 py-3">Tanggal</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(list || []).map((b) => (
              <tr key={b.id} data-testid={`blog-row-${b.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <p className="font-semibold max-w-md truncate">{b.title}</p>
                  <p className="text-xs text-slate-400 max-w-md truncate">{b.excerpt}</p>
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{b.date_label}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${b.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid={`blog-status-${b.id}`}>
                    {b.is_published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <button onClick={() => togglePublish(b)} data-testid={`blog-publish-${b.id}`} title={b.is_published ? "Sembunyikan" : "Publish"} className="p-1.5 text-slate-400 hover:text-emerald-600">
                    {b.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => { setForm({ title: b.title, excerpt: b.excerpt || "", image: b.image || "", image_ratio: b.image_ratio || "16:9", content: b.content || "", is_published: b.is_published, date_label: b.date_label || "", read_time: b.read_time || "5 menit baca" }); setEditId(b.id); }} data-testid={`blog-edit-${b.id}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(b.id)} data-testid={`blog-delete-${b.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {list && list.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Belum ada artikel.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" data-testid="blog-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Artikel" : "Tulis Artikel Baru"}</h2>
              <button onClick={() => setForm(null)} data-testid="blog-close-dialog"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Judul Artikel *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ringkasan (tampil di kartu daftar)</label>
                <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Gambar Sampul</label>
                <div className="flex gap-2 items-center">
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="blog-image" className={inputCls} placeholder="URL gambar atau upload →" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="blog-image-upload" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-300 text-xs font-bold rounded-md hover:border-[#C9A227] hover:text-[#C9A227] transition-colors">
                    <Upload className="h-4 w-4" /> {uploading ? "..." : "Upload"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImg} />
                </div>
                {form.image && (
                  <div className={`w-40 max-w-full mt-2 rounded border border-slate-200 overflow-hidden bg-slate-100 ${imageRatioClass(form.image_ratio, "16:9")}`}>
                    <img src={imgSrc(form.image)} alt="sampul" className="w-full h-full object-cover" />
                  </div>
                )}
                <ImageRatioPicker value={form.image_ratio} onChange={(value) => setForm({ ...form, image_ratio: value })} defaultRatio="16:9" testid="blog-image-ratio" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Label Tanggal</label>
                  <input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} data-testid="blog-date" className={inputCls} placeholder="Kosongkan = otomatis hari ini" />
                </div>
                <div>
                  <label className={labelCls}>Estimasi Baca</label>
                  <input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} data-testid="blog-readtime" className={inputCls} placeholder="5 menit baca" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Isi Artikel *</label>
                <textarea rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content" className={inputCls + " font-mono-accent text-xs leading-relaxed"} placeholder={"## Judul Bagian\nTulis paragraf di sini...\n- Poin daftar\n| Kolom 1 | Kolom 2\n| Isi | Isi"} />
                <p className="text-[10px] text-slate-400 mt-1.5">Format: <code>## Judul Bagian</code> untuk heading • <code>- teks</code> untuk poin daftar • baris <code>| a | b |</code> berurutan menjadi tabel (baris pertama = header) • baris lain = paragraf.</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="blog-published" className="h-4 w-4 accent-[#0B1F3A]" />
                Publish (tampil di website)
              </label>
              <button onClick={save} disabled={!form.title || !form.content} data-testid="save-blog" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                {editId ? "Simpan Perubahan" : "Terbitkan Artikel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
